import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import type { AppNotification } from "@/api/types";
import { Skeleton, Spinner } from "@/components/ui";
import { cn } from "@/lib/cn";
import { prefersReducedMotion } from "@/motion/useReducedMotion";
import { useMarkRead, useNotifications, useUnreadNotifications } from "./api";
import { NotificationItem } from "./NotificationItem";

/** Bell with unread badge; opens the latest notifications. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const unread = useUnreadNotifications().data ?? 0;
  const list = useNotifications(false);
  const markRead = useMarkRead();
  const items = (list.data?.pages[0]?.results ?? []).slice(0, 8);
  const bell = useRef<HTMLSpanElement>(null);
  const lastCount = useRef(unread);

  // A new item arrived: a quick bounce on the bell.
  useEffect(() => {
    if (unread > lastCount.current && bell.current && !prefersReducedMotion()) {
      bell.current.animate?.(
        [
          { transform: "translateY(0) rotate(0)" },
          { transform: "translateY(-6px) rotate(-14deg)", offset: 0.27 },
          { transform: "translateY(0) rotate(10deg)", offset: 0.54 },
          { transform: "rotate(-6deg)", offset: 0.77 },
          { transform: "rotate(0)" },
        ],
        { duration: 520, easing: "ease-out" },
      );
    }
    lastCount.current = unread;
  }, [unread]);

  useEffect(() => {
    if (!open) return;
    void list.refetch();
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openNote = (note: AppNotification) => {
    if (!note.is_read) markRead.mutate([note.id]);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        className="relative grid size-10 place-items-center rounded-full text-ink-soft transition hover:bg-fg/5 hover:text-ink"
      >
        <span ref={bell} className="grid place-items-center">
          <Bell className={cn("size-5", unread > 0 && "motion-safe:animate-wiggle")} aria-hidden />
        </span>
        {unread > 0 && (
          <span
            className="tabular absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-[var(--magenta-strong)] px-1 text-[11px] font-semibold text-white shadow-[0_0_14px_rgb(236_72_153/0.6)]"
            aria-hidden
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="fixed inset-x-3 top-[calc(var(--safe-top)+var(--header-h)+8px)] z-50 max-h-[calc(100dvh-var(--app-top)-var(--app-bottom)-16px)] animate-pop-in overflow-y-auto rounded-card border border-[var(--border-glow)] bg-elevated shadow-glow-2 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[380px]"
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="font-semibold">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate(undefined)}
                className="inline-flex items-center gap-1 text-xs text-[var(--primary-text)] hover:underline"
              >
                {markRead.isPending ? <Spinner className="size-3" label="Marking" /> : <CheckCheck className="size-3.5" aria-hidden />}
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-1.5">
            {list.isLoading ? (
              <div className="flex flex-col gap-2 p-2">
                <Skeleton className="h-14" />
                <Skeleton className="h-14" />
              </div>
            ) : items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-ink-muted">You're all caught up.</p>
            ) : (
              items.map((n) => <NotificationItem key={n.id} note={n} compact onOpen={openNote} />)
            )}
          </div>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-line px-4 py-3 text-center text-sm text-[var(--primary-text)] hover:bg-fg/[0.03]"
          >
            See all notifications
          </Link>
        </div>
      )}
    </div>
  );
}
